import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Renderer2,
} from '@angular/core';

import { TickerInterval, TickerService } from '@frontend/sports/common/core/utils/timer';
import { padStart } from 'lodash-es';
import { Subscription } from 'rxjs';

@Component({
    selector: 'ms-countdown-timer',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class CountdownTimerComponent implements OnChanges, OnDestroy {
    @Input() timeInSeconds: number;
    @Input() thresholdSeconds: number;
    @Input() hideAfterComplete = false;
    @Input() textAfterTimerFinished = '';
    @Input() isSports: boolean;
    @Input() showTimerInSeconds: boolean;

    @Output() shown = new EventEmitter<void>();
    @Output() finish = new EventEmitter<void>();
    @Output() display = new EventEmitter<boolean>();

    private tickerSubscription: Subscription;
    protected readonly MINUTE = 60;
    protected readonly HOUR = 60 * this.MINUTE;

    @HostBinding('class.hidden') hidden = true;

    @HostBinding('class') get class(): string {
        return this.isSports ? 'countdown-timer' : 'betstation-countdown-timer';
    }

    constructor(
        private ticker: TickerService,
        private elementRef: ElementRef,
        private changeDetector: ChangeDetectorRef,
        private renderer: Renderer2,
    ) {}

    ngOnChanges(): void {
        this.elementRef.nativeElement.textContent = '';
        this.renderer.setStyle(this.elementRef.nativeElement, 'color', 'inherit');

        if (this.timeInSeconds) {
            this.hidden = true;
            this.startTicker();
            this.setCountdownTime();
        }
    }

    ngOnDestroy(): void {
        this.stopTicker();
    }

    private startTicker(): void {
        this.stopTicker();
        this.tickerSubscription = this.ticker.subscribe(() => this.setCountdownTime(), TickerInterval.Second);
    }

    private stopTicker(): void {
        this.tickerSubscription?.unsubscribe();
    }

    private setCountdownTime(): void {
        if (this.timeInSeconds <= 0) {
            this.stopTicker();
            this.hidden = this.hideAfterComplete;
            this.finish.emit();
            if (this.textAfterTimerFinished) {
                this.elementRef.nativeElement.textContent = this.textAfterTimerFinished;
            }
            this.changeDetector.markForCheck();

            return;
        }

        const timeLeft = this.timeInSeconds--;

        if (timeLeft >= this.thresholdSeconds) {
            return;
        }

        if (this.hidden) {
            this.hidden = false;
            this.shown.emit();
            this.changeDetector.markForCheck();
        }

        this.elementRef.nativeElement.textContent = this.isSports ? this.formatTime(timeLeft) : this.betStationFormatTime(timeLeft);
    }

    private formatTime(timeSpan: number): string {
        const hours = Math.floor(timeSpan / this.HOUR);
        const minutes = Math.floor(timeSpan / this.MINUTE) % this.MINUTE;
        const seconds = timeSpan % this.MINUTE;

        if (this.showTimerInSeconds) {
            return ` ${seconds} `;
        }

        if (hours) {
            this.display.emit(false);

            return '';
        }
        this.display.emit(true);

        if (minutes < 10) {
            this.renderer.setStyle(this.elementRef.nativeElement, 'color', 'red');
        }

        return ` ${this.pad(minutes)}:${this.pad(seconds)}`;
    }

    private pad(val: number): string {
        return padStart(val.toString(), 2, '0');
    }

    private betStationFormatTime(timeSpan: number): string {
        const hours = Math.floor(timeSpan / this.HOUR);
        const minutes = Math.floor(timeSpan / this.MINUTE) % this.MINUTE;
        const seconds = timeSpan % this.MINUTE;
        const hoursText = (hours && `${hours}h`) || '';

        const time = `${hoursText} ${minutes}m ${this.pad(seconds)}s`;
        this.display.emit(!!time);

        return time;
    }
}
